import React, { useState } from 'react';
import * as yup from 'yup';
import axios from 'axios';
import { Formik } from 'formik';
import Banner from '@ordermentum/ui-shared/atoms/Banner';
import LabelledInput from '@ordermentum/ui-shared/molecules/LabelledInput';
import { Link } from 'react-router';
import { SignInContainer } from '../LoginBase.styles';
import {
  EmailLink,
  ErrorBannerWrapper,
  FieldWrapper,
  FormWrapper,
  SignInLabelContainer,
  SignInSwitch,
  SignInText,
} from './index.styles';
import { SubmissionButton } from './LoginForm';
import { PasswordResetFormProps } from './index.types';

const getStateError = (state?: string) => {
  if (state === 'expired') {
    return "Unfortunately this reset link doesn't work. Please request a new one.";
  }

  if (state === 'invalid') {
    return "Unfortunately this reset link doesn't work. Please request a new one.";
  }
  if (state === 'unknown') {
    return 'Please enter an email address to reset your password.';
  }
  return undefined;
};

const PasswordResetForm = ({ state, onBackClick }: PasswordResetFormProps) => {
  const stateError = getStateError(state);
  const [reqError, setReqError] = useState<string | undefined>(stateError);

  const [success, setSuccessState] = useState<boolean>(false);

  const BackLink = ({ children }: React.PropsWithChildren<{}>) =>
    onBackClick ? (
      <a onClick={onBackClick}>{children}</a>
    ) : (
      <Link to="/">{children}</Link>
    );

  const PassowrdResetSuccess = () => (
    <FormWrapper data-testid="password-reset-success-info">
      <FieldWrapper>
        Please check your email. We&apos;ve sent you a link to reset your
        password.
      </FieldWrapper>
      <FieldWrapper>
        If you haven&apos;t got the email, please check your spam folder. If you
        haven&apos;t received anything within 10 minutes please{' '}
        <EmailLink href="mailto:support@ordermentum.com">contact us</EmailLink>{' '}
        and we&apos;ll help you out.
      </FieldWrapper>
    </FormWrapper>
  );

  const ResetForm = ({ formik }) => (
    <form onSubmit={formik.handleSubmit}>
      <FormWrapper>
        <FieldWrapper>
          Enter your email and we&apos;ll send you a link to reset your
          password.
        </FieldWrapper>
        <FieldWrapper>
          <LabelledInput
            label="Email address"
            required
            tag="email"
            type="email"
            errorMessage="Please enter a valid email address"
            hasError={formik.errors.email && formik.touched.email}
            value={formik.values.email}
            onChange={formik.setFieldValue}
            size="max"
          />
        </FieldWrapper>
        <FieldWrapper data-testid="reset-submit-button">
          <SubmissionButton loading={formik.isSubmitting}>
            Reset password
          </SubmissionButton>
        </FieldWrapper>
      </FormWrapper>
    </form>
  );

  return (
    <SignInContainer>
      <SignInLabelContainer>
        <SignInText>Reset Password</SignInText>
        <SignInSwitch data-testid="signin-link">
          <BackLink>sign in</BackLink>
        </SignInSwitch>
      </SignInLabelContainer>
      {reqError && (
        <ErrorBannerWrapper>
          <Banner type="warning" noIcon dismiss={false}>
            {reqError}
          </Banner>
        </ErrorBannerWrapper>
      )}
      <Formik
        initialValues={{
          email: '',
        }}
        validationSchema={yup.object().shape({
          email: yup.string().email().required(),
        })}
        onSubmit={async values => {
          setReqError(undefined);
          try {
            await axios.post('/user/password', {
              email: values.email,
            });
            setSuccessState(true);
          } catch (err) {
            setReqError(err.response.data.error);
          }
        }}
      >
        {formik =>
          success ? <PassowrdResetSuccess /> : <ResetForm formik={formik} />
        }
      </Formik>
    </SignInContainer>
  );
};

export default PasswordResetForm;
